import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseTool from '@glass-designer/tools/baseTool'
import EditingItem from '@glass-designer/objects/editingItem'
import GlassPanelItem from '@glass-designer/model/glassPanelItem'
import DatumLink from '@glass-designer/model/datumLink'

import GlassDesignerTypes from '@glass-designer/types'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'


/**
 * Add Panel Item Tool
 * Tool started when dragging a panel item from the library into the editor
 */
export default class AddPanelItemTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.addPanelItem
    this.draggingItemActive = false

    this.datumLinks = []
    this.panelItem = null

    this.itemObject = new EditingItem(editor)
    this.itemObject.setVisible(false)
    this.addChildObject(this.itemObject)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive() {
    super.onToolActive()

    // Reset
    this.draggingItemActive = false
    this.itemObject.setVisible(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.draggingItemActive = false
    this.itemObject.setVisible(false)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.draggingItemActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {

        this.itemObject.setPosition(rayInt)
      }

      let shape = this.editor.findClosestGlassShape(this.itemObject.position, { ignoreStockLinked: true })
      if(shape) {

        // Calculate the 2 datum reference objects in the shape
        // Either segments or other holes
        this._calculateItemDatumLinks(shape, this.panelItem, this.itemObject.position)
        this.itemObject.setDatumLinks(this.datumLinks)

        // Update the dimensions for the item to the shape
        this.editor.onToolEditingUpdated(shape, [this.itemObject])

        this.editor.flagRenderRequired()
      }
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(this.draggingItemActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {
        this.itemObject.setPosition(rayInt)
      }

      let shape = this.editor.findClosestGlassShape(this.itemObject.position)
      if(shape) {

        // Calculate the 2 datum reference objects in the shape
        // Either segments or other holes
        this._calculateItemDatumLinks(shape, this.panelItem, this.itemObject.position)

        this._placePanelItem(shape)

        this.editor.flagRenderRequired()
      }
    }
  }


  /**
   * A library item was dragged into the editor while this tool was active
   */
  libraryItemDraggedToEditor(itemInfo) {
    this.draggingItemActive = true

    this.datumLinks = []
    let itemData = itemInfo.itemData || {}

    // Panel Item
    this.panelItem = new GlassPanelItem(this.editor, {
      width: parseInt(itemData.width || 100),
      height: parseInt(itemData.height || 50),
      angle: parseInt(itemData.angle || 0),
      stockId: parseInt(itemInfo.stockId || 0),
      orderEditorLibraryItemId: itemInfo.id
    })
    this.panelItem.setVisible(false)

    this.itemObject.buildFromLibraryItemInfo(itemInfo)
    this.itemObject.setVisible(true)

    this.editor.flagRenderRequired()
  }


  /**
   * Release the panel item & place a new panel item onto the shape
   */
  _placePanelItem(shape) {
    this.draggingItemActive = false
    this.itemObject.setVisible(false)

    // Datum links to segments / other holes
    this.panelItem.setVisible(true)

    this.editor.startUndoGroup('Adding panel item')
    this.editor.addUndoShape(shape)

    try {
      this.panelItem.setDatumLinks(this.datumLinks)
      shape.addPanelItem(this.panelItem)

      this.editor.closeCurrentUndoGroup()

      this.panelItem = null
      this.closeTool()

    } catch(err) {
      this.editor.cancelCurrentUndoGroup()

      // Display error (eg, panel item outside of shape or whatever)
      if(err instanceof EditorUserError)
        this.editor.component.errorMessage('Error placing item', err.message)

      console.error(err)
    }

    // Rebuild dimensions for the shape
    this.editor.onShapeUpdated(shape)

    // Reset dimensions for editing
    this.editor.dimensionHelper.resetEditingDimensions()

    this.panelItem = null
  }


  /**
   * Calculate the best datum links for an object at its current position relative to the selected shape
   * Updates datumLinks array
   * Attempts to find 2 best datum links
   */
  _calculateItemDatumLinks(shape, hole, position) {
    if(this.datumLinks.length == 0) {
      this.datumLinks.push(new DatumLink())
      this.datumLinks.push(new DatumLink())
    }

    EditorDatumUtils.calculateClosestDatumLinks(shape, this.datumLinks, position, { ignoreAllHoles: true })
  }
}